import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { useTheme } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Pencil, Trash2, FileText, Search } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "");

export default function TiposDocumentoPage() {
  const { darkMode } = useTheme();
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: ""
  });

  const fetchTipos = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/mensajeria/tipos-documento`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTipos(response.data);
    } catch (error) {
      toast.error("Error al cargar tipos de documento");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTipos();
  }, [fetchTipos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingTipo) {
        await axios.put(
          `${API}/api/mensajeria/tipos-documento/${editingTipo.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Tipo de documento actualizado");
      } else {
        await axios.post(
          `${API}/api/mensajeria/tipos-documento`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Tipo de documento creado");
      }
      setDialogOpen(false);
      resetForm();
      fetchTipos();
    } catch (error) {
      toast.error("Error al guardar tipo de documento");
    }
  };

  const handleEdit = (tipo) => {
    setEditingTipo(tipo);
    setFormData({
      nombre: tipo.nombre,
      descripcion: tipo.descripcion || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este tipo de documento?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api/mensajeria/tipos-documento/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tipo de documento eliminado");
      fetchTipos();
    } catch (error) {
      toast.error("Error al eliminar tipo de documento");
    }
  };

  const resetForm = () => {
    setFormData({ nombre: "", descripcion: "" });
    setEditingTipo(null);
  };

  const filteredTipos = tipos.filter(tipo =>
    tipo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tipo.descripcion && tipo.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className={`min-h-screen flex ${darkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className={`text-3xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                Tipos de Documento
              </h1>
              <p className={`mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                Gestiona los tipos de documento para solicitudes de mensajería
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-tipo-btn" className="bg-[#45E69A] hover:bg-[#93bc2e] text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuevo Tipo
                </Button>
              </DialogTrigger>
              <DialogContent className={darkMode ? "bg-gray-800 border-gray-700" : ""}>
                <DialogHeader>
                  <DialogTitle className={darkMode ? "text-white" : ""}>
                    {editingTipo ? "Editar Tipo de Documento" : "Nuevo Tipo de Documento"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className={darkMode ? "text-gray-300" : ""}>Nombre *</Label>
                    <Input
                      data-testid="tipo-nombre-input"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Ej: Factura, Contrato, Póliza..."
                      className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                      required
                    />
                  </div>
                  <div>
                    <Label className={darkMode ? "text-gray-300" : ""}>Descripción</Label>
                    <Textarea
                      data-testid="tipo-descripcion-input"
                      value={formData.descripcion}
                      onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                      placeholder="Descripción opcional del tipo de documento"
                      className={darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDialogOpen(false);
                        resetForm();
                      }}
                      className={darkMode ? "border-gray-600 text-gray-300" : ""}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-tipo-btn"
                      className="bg-[#45E69A] hover:bg-[#93bc2e] text-black"
                    >
                      {editingTipo ? "Actualizar" : "Crear"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Search */}
          <Card className={`mb-6 ${darkMode ? "bg-gray-800 border-gray-700" : ""}`}>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
                <Input
                  data-testid="search-tipos"
                  placeholder="Buscar tipos de documento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 ${darkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}
                />
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className={darkMode ? "bg-gray-800 border-gray-700" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Total Tipos</p>
                    <p className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {tipos.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={darkMode ? "bg-gray-800 border-gray-700" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <FileText className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Resultados</p>
                    <p className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>
                      {filteredTipos.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <Card className={darkMode ? "bg-gray-800 border-gray-700" : ""}>
            <CardHeader>
              <CardTitle className={darkMode ? "text-white" : ""}>Lista de Tipos</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin h-8 w-8 border-4 border-[#45E69A] border-t-transparent rounded-full mx-auto"></div>
                  <p className={`mt-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Cargando...</p>
                </div>
              ) : filteredTipos.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className={`h-12 w-12 mx-auto mb-4 ${darkMode ? "text-gray-600" : "text-gray-400"}`} />
                  <p className={darkMode ? "text-gray-400" : "text-gray-600"}>
                    {searchTerm ? "No se encontraron tipos de documento" : "No hay tipos de documento"}
                  </p>
                  {!searchTerm && (
                    <Button
                      onClick={() => setDialogOpen(true)}
                      variant="outline"
                      className="mt-4"
                    >
                      Crear primer tipo
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={darkMode ? "border-gray-700" : ""}>
                      <TableHead className={darkMode ? "text-gray-300" : ""}>Nombre</TableHead>
                      <TableHead className={darkMode ? "text-gray-300" : ""}>Descripción</TableHead>
                      <TableHead className={darkMode ? "text-gray-300" : ""}>Fecha Creación</TableHead>
                      <TableHead className={`text-right ${darkMode ? "text-gray-300" : ""}`}>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTipos.map((tipo) => (
                      <TableRow key={tipo.id} className={darkMode ? "border-gray-700" : ""}>
                        <TableCell className={`font-medium ${darkMode ? "text-white" : ""}`}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[#45E69A]" />
                            {tipo.nombre}
                          </div>
                        </TableCell>
                        <TableCell className={darkMode ? "text-gray-300" : "text-gray-600"}>
                          {tipo.descripcion || "-"}
                        </TableCell>
                        <TableCell className={darkMode ? "text-gray-300" : "text-gray-600"}>
                          {new Date(tipo.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(tipo)}
                            className={darkMode ? "text-gray-300 hover:text-white" : ""}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(tipo.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
